import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import InputDatePicker from "../../../../EvaluationNew/components/InputDatePicker";
import FormSignatureSections from "../FormSignatureSections";
import useFormSignatures from "../useFormSignatures";
import {
  FORM_FLAG_MESSAGES,
  CheckboxGrid,
  ExclusiveChoiceGrid,
  Field,
  FormActionBar,
  FormIdentityStrip,
  FormLoadingSkeleton,
  FormMetaRow,
  FormPageHeader,
  LockedReasonBanner,
  OfficeCommentsNote,
  OfficeReviewSection,
  SHEET,
  Section,
  SubHeading,
  fieldClass,
  textareaClass,
} from "../formLayout";
import {
  createPatientClinicalForm,
  getOtVisitNoteForm,
  updateOtVisitNoteForm,
} from "../../../../../services/patientService";
import {
  DeleteIcon,
  FileCheckIcon,
  HeartPulseIcon,
  HelpingIcon,
  MedicalNotesIcon,
  PlusIcon,
  RunningIcon,
  TargetIcon,
} from "../../../../../Icons";
import { displayDateUS, formatDateForInput } from "../../../../../utils/dateUtils";
import { PATIENT_PAGE_SHELL } from "../../../patientPageLayout";
import { saveDedicatedForm } from "../clinicalFormDraft";
import {
  draftCaregiverIdentity,
  useFormCaregiverPicker,
} from "../formCaregiver";
import { buildClinicalFormPermissions } from "../clinicalFormPermissions";
import { useClinicalCosignPermissions } from "../hooks/useClinicalCosignPermissions";
import {
  applyDisciplineFormPermissions,
  resolveFormActorRole,
  OT_FORM_EDITOR_ROLES,
} from "../disciplineFormAccess";
import {
  ADL_IMPACTED_OPTIONS,
  AMBULATION_STATUS_OPTIONS,
  COGNITION_STATUS_OPTIONS,
  HOME_SAFETY_OPTIONS,
  MOBILITY_TREND_OPTIONS,
  OT_VISIT_ADL_SCORE_OPTIONS,
  OT_VISIT_ADL_TASKS,
  OT_VISIT_ARRAY_KEYS,
  OT_VISIT_INTERVENTION_OPTIONS,
  OT_VISIT_UE_METRICS,
  PATIENT_RESPONSE_OPTIONS,
  PROGRESS_TOWARD_GOALS_OPTIONS,
  REASON_FOR_VISIT_OPTIONS,
  TRANSFER_STATUS_OPTIONS,
  VISIT_TYPE_OPTIONS,
  YES_NO_OPTIONS,
  YES_NO_SOME_OPTIONS,
  emptyOtVisitAdlScores,
  emptyOtVisitPayload,
  emptyOtVisitUeRows,
  parsePainLevel,
  toggleInList,
} from "./otVisitNoteContent";
import {
  emptyTextGoalRow,
  normalizeTextGoals,
  renumberTextGoals,
  serializeTextGoals,
} from "../ptNotes/ptGoals";
import {
  assertOtVisitMetaForFlag,
  buildOtVisitMetaProps,
  pickOtVisitMeta,
} from "./otVisitCommon";
import OtNotesCommonHeader from "./OtNotesCommonHeader";
import PainScalePills from "../ptNotes/PainScalePills";
import {
  normalizeOtCommonHeaderState,
  resolveOtNotesCommonHeader,
  withNormalizedOtCommonHeader,
} from "./otNotesCommonHeaderUtils";

const VISIT_DATE_MAX = new Date().toISOString().slice(0, 10);
const today = () => new Date().toISOString().slice(0, 10);

const emptyValues = () => ({
  ...emptyOtVisitPayload(),
  visitDate: today(),
  clinicianSignatureName: "",
  patientSignatureName: "",
  reviewComments: "",
});

const DateFieldInput = ({ name, value, canEdit, printMode, onChange, max }) => {
  if (printMode) {
    return (
      <div className={`${fieldClass} flex items-center`}>
        {displayDateUS(value) || "—"}
      </div>
    );
  }
  return (
    <InputDatePicker
      name={name}
      formatSeparator="/"
      allowManualEntry
      disabled={!canEdit}
      value={formatDateForInput(value) || ""}
      onChange={(event) => onChange(event.target.value)}
      max={max}
      className="w-full"
      inputClassName={`${fieldClass} bg-white`}
    />
  );
};

DateFieldInput.propTypes = {
  name: PropTypes.string.isRequired,
  value: PropTypes.string,
  canEdit: PropTypes.bool,
  printMode: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  max: PropTypes.string,
};

const SelectField = ({ value, options, canEdit, onChange, placeholder }) => (
  <select
    className={fieldClass}
    value={value ?? ""}
    disabled={!canEdit}
    onChange={(e) => onChange(e.target.value)}
  >
    <option value="">{placeholder || "—"}</option>
    {options.map((opt) => (
      <option key={opt} value={opt}>
        {opt}
      </option>
    ))}
  </select>
);

SelectField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  ).isRequired,
  canEdit: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};

const Panel = ({ title, children }) => (
  <div className="h-full rounded-md border border-slate-200 bg-white flex flex-col min-h-0">
    <div className="flex items-center px-2.5 py-1.5 border-b border-sky-100 bg-sky-50/80">
      <p className="text-[11px] font-semibold text-slate-800">{title}</p>
    </div>
    <div className="p-2.5 flex-1 min-h-0">{children}</div>
  </div>
);

Panel.propTypes = {
  title: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const normalizeAdlScores = (src) => {
  const incoming = src && typeof src === "object" && !Array.isArray(src) ? src : {};
  const base = emptyOtVisitAdlScores();
  return Object.fromEntries(
    OT_VISIT_ADL_TASKS.map((task) => [task, incoming[task] || base[task] || ""]),
  );
};

const normalizeUeFunction = (rows) => {
  const byMetric = Object.fromEntries(
    (Array.isArray(rows) ? rows : []).map((row) => [row.metric, row]),
  );
  return OT_VISIT_UE_METRICS.map((metric) => ({
    ...emptyOtVisitUeRows().find((r) => r.metric === metric),
    ...(byMetric[metric] || {}),
    metric,
  }));
};

const OtVisitNoteForm = ({
  patient,
  clinicalForm,
  draft = null,
  onBack,
  onCreated,
  printMode = false,
  onReady,
}) => {
  const patientId = clinicalForm.patientId;
  const formId = clinicalForm.id || null;
  const isDraft = !formId;

  const [form, setForm] = useState(null);
  const [values, setValues] = useState(emptyValues);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);

  const signatures = useFormSignatures();
  const { reset: resetSignatures } = signatures;

  const currentUser = useMemo(
    () => JSON.parse(localStorage.getItem("currentUser") || "{}"),
    [],
  );
  const role = resolveFormActorRole(currentUser);

  const patientName = useMemo(
    () => [patient?.lastName, patient?.firstName].filter(Boolean).join(", "),
    [patient?.lastName, patient?.firstName],
  );

  const hydrate = useCallback(
    (data) => {
      setForm(data);
      const base = emptyOtVisitPayload();
      const loadedPayload =
        data.payload && typeof data.payload === "object" && !Array.isArray(data.payload)
          ? data.payload
          : {};

      const merged = {
        ...base,
        ...loadedPayload,
        ...pickOtVisitMeta(loadedPayload, data),
        visitDate: data.visitDate || loadedPayload.visitDate || today(),
        commonHeader: normalizeOtCommonHeaderState(
          loadedPayload.commonHeader || base.commonHeader,
        ),
        adlScores: normalizeAdlScores(loadedPayload.adlScores),
        ueFunction: normalizeUeFunction(loadedPayload.ueFunction),
        painPreVisit: parsePainLevel(loadedPayload.painPreVisit),
        painPostVisit: parsePainLevel(loadedPayload.painPostVisit),
        nextVisitDate: formatDateForInput(loadedPayload.nextVisitDate) || "",
        shortTermGoals: normalizeTextGoals(loadedPayload, {
          arrayKey: "shortTermGoals",
          legacyPrefix: "shortTermGoal",
          kind: "st",
          minCount: 3,
        }),
        longTermGoals: normalizeTextGoals(loadedPayload, {
          arrayKey: "longTermGoals",
          legacyPrefix: "longTermGoal",
          kind: "lt",
          minCount: 3,
        }),
        patientSignatureName:
          data.patientSignatureName || data.patientName || patientName,
        clinicianSignatureName: data.clinicianSignatureName || "",
        reviewComments: data.reviewComments || "",
      };

      OT_VISIT_ARRAY_KEYS.forEach((key) => {
        merged[key] = Array.isArray(loadedPayload[key])
          ? loadedPayload[key].map(String)
          : Array.isArray(base[key])
            ? base[key]
            : [];
      });

      for (let n = 1; n <= 12; n += 1) {
        delete merged[`shortTermGoal${n}`];
        delete merged[`longTermGoal${n}`];
      }

      setValues(merged);
      resetSignatures();
    },
    [patientName, resetSignatures],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const finish = () => {
      if (cancelled) return;
      setLoading(false);
      onReady?.();
    };

    const load = async () => {
      try {
        if (isDraft) {
          const commonHeader = await resolveOtNotesCommonHeader({
            patient,
            patientId,
            payload: {},
          });
          if (cancelled) return;
          hydrate({
            formName:
              clinicalForm.formName ||
              draft?.formName ||
              "Occupational Therapy Visit Note",
            formType: clinicalForm.formType || draft?.formType || "ot_visit_note",
            formSection: clinicalForm.formSection || draft?.formSection,
            status: "pending",
            patientName,
            patientDateOfBirth: patient?.dateOfBirth || null,
            ...draftCaregiverIdentity(currentUser, role),
            createdAt: new Date().toISOString(),
            permissions: buildClinicalFormPermissions("pending", role),
            visitDate: today(),
            clinicianSignatureName: "",
            patientSignatureName: patientName,
            reviewComments: "",
            payload: {
              ...emptyOtVisitPayload(),
              commonHeader,
            },
          });
          return;
        }

        const res = await getOtVisitNoteForm(patientId, formId);
        if (cancelled) return;
        const loaded = res.result?.form || res.form;
        const existingPayload =
          loaded.payload && typeof loaded.payload === "object"
            ? loaded.payload
            : {};
        const commonHeader = await resolveOtNotesCommonHeader({
          patient,
          patientId,
          payload: existingPayload,
        });
        if (cancelled) return;
        hydrate({
          ...loaded,
          payload: {
            ...existingPayload,
            commonHeader,
          },
        });
      } catch (err) {
        if (cancelled) return;
        toast.error(err.message || "Failed to load form");
        onBack();
      } finally {
        finish();
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, formId, isDraft]);

  const setValue = (key, value) =>
    setValues((prev) => ({ ...prev, [key]: value }));

  const toggleListValue = (key, item) => {
    setValues((prev) => ({
      ...prev,
      [key]: toggleInList(prev[key], item),
    }));
  };

  const setAdlScore = (task, score) => {
    setValues((prev) => ({
      ...prev,
      adlScores: { ...(prev.adlScores || {}), [task]: score },
    }));
  };

  const updateUeRow = (metric, patch) => {
    setValues((prev) => ({
      ...prev,
      ueFunction: (prev.ueFunction || []).map((row) =>
        row.metric === metric ? { ...row, ...patch } : row,
      ),
    }));
  };

  const updateTextGoal = (listKey, goalId, text) => {
    setValues((prev) => ({
      ...prev,
      [listKey]: (prev[listKey] || []).map((row) =>
        row.id === goalId ? { ...row, text } : row,
      ),
    }));
  };

  const addTextGoal = (listKey, kind) => {
    setValues((prev) => {
      const current = prev[listKey] || [];
      return {
        ...prev,
        [listKey]: renumberTextGoals(
          [...current, emptyTextGoalRow(current.length + 1, kind)],
          kind,
        ),
      };
    });
  };

  const removeTextGoal = (listKey, kind, goalId) => {
    setValues((prev) => {
      const current = prev[listKey] || [];
      if (current.length <= 1) return prev;
      return {
        ...prev,
        [listKey]: renumberTextGoals(
          current.filter((row) => row.id !== goalId),
          kind,
        ),
      };
    });
  };

  const { permissions: basePermissions } = useClinicalCosignPermissions({
    status: form?.status || "pending",
    role,
    actorUserId: currentUser?.id ? Number(currentUser.id) : null,
    formFilledByUserId:
      form?.filledBy ||
      clinicalForm?.filledBy ||
      (currentUser?.id ? Number(currentUser.id) : null),
    formFilledByRole: form?.filledByRole || clinicalForm?.filledByRole || role,
    serverPermissions: form?.permissions,
  });

  const permissions = useMemo(
    () =>
      applyDisciplineFormPermissions(
        basePermissions,
        role,
        OT_FORM_EDITOR_ROLES,
        "View only — only OT/OTA can edit this form.",
      ),
    [basePermissions, role],
  );

  const canEdit = !printMode && Boolean(permissions.canEdit);
  const isReviewer = Boolean(permissions.canReview);
  const canSign = !printMode && Boolean(permissions.canSignSignatures);

  const {
    caregiverSlot,
    filledByUserId,
    assertCaregiverForCreate,
  } = useFormCaregiverPicker({
    patientId,
    formType: form?.formType || draft?.formType || clinicalForm?.formType || "ot_visit_note",
    role,
    canEdit,
    preferredStaffId: draft?.visit?.staffId ?? null,
    currentFilledByUserId: form?.filledBy || clinicalForm?.filledBy || null,
  });

  const handleClinicianSign = (signature, signerName) => {
    signatures.handleClinicianSign(signature);
    setValues((prev) => ({
      ...prev,
      clinicianSignatureName: signerName || prev.clinicianSignatureName,
    }));
  };

  const buildPayload = (flag) => {
    const {
      clinicianSignatureName,
      patientSignatureName,
      reviewComments,
      visitDate,
      ...fields
    } = values;

    const nestedPayload = withNormalizedOtCommonHeader({
      ...fields,
      visitDate: visitDate ? formatDateForInput(visitDate) : "",
      nextVisitDate: values.nextVisitDate
        ? formatDateForInput(values.nextVisitDate)
        : "",
      adlScores: normalizeAdlScores(values.adlScores),
      ueFunction: normalizeUeFunction(values.ueFunction),
      painPreVisit: parsePainLevel(values.painPreVisit),
      painPostVisit: parsePainLevel(values.painPostVisit),
      shortTermGoals: serializeTextGoals(values.shortTermGoals),
      longTermGoals: serializeTextGoals(values.longTermGoals),
    });

    OT_VISIT_ARRAY_KEYS.forEach((key) => {
      nestedPayload[key] = Array.isArray(values[key]) ? values[key] : [];
    });

    for (let n = 1; n <= 12; n += 1) {
      delete nestedPayload[`shortTermGoal${n}`];
      delete nestedPayload[`longTermGoal${n}`];
    }

    const payload = {
      flag,
      visitDate: visitDate ? formatDateForInput(visitDate) : null,
      billingCode: values.billingCode || "",
      timeIn: values.timeIn || "",
      timeOut: values.timeOut || "",
      payload: nestedPayload,
      clinicianSignatureName: clinicianSignatureName || "",
      patientSignatureName: patientSignatureName || "",
      reviewComments: reviewComments || "",
    };

    if (!canSign) return payload;
    return { ...payload, ...signatures.buildSignaturePayload() };
  };

  const submit = async (flag) => {
    if (flag === "to_be_corrected" && !values.reviewComments?.trim()) {
      toast.error("Add review comments before marking the form to be corrected");
      return;
    }
    const visitMetaError = assertOtVisitMetaForFlag(values, flag);
    if (visitMetaError) {
      toast.error(visitMetaError);
      return;
    }
    const caregiverError = assertCaregiverForCreate();
    if (caregiverError) {
      toast.error(caregiverError);
      return;
    }
    setSaving(flag);
    try {
      const { form: saved, createdClinicalFormId } = await saveDedicatedForm({
        patientId,
        clinicalForm,
        draft,
        flag,
        payload: buildPayload(flag),
        createClinicalForm: createPatientClinicalForm,
        updateDedicatedForm: updateOtVisitNoteForm,
        filledByUserId,
        scheduleDate: formatDateForInput(values.visitDate) || today(),
      });
      hydrate(saved);
      toast.success(FORM_FLAG_MESSAGES[flag] || "Form updated");
      if (createdClinicalFormId) onCreated?.(createdClinicalFormId);
    } catch (err) {
      toast.error(err.message || "Failed to save form");
    } finally {
      setSaving(null);
    }
  };

  if (loading) return <FormLoadingSkeleton />;
  if (!form) return null;

  const signatureProps = signatures.sectionProps(form);
  const showReasonOther = (values.reasonForVisit || []).includes("Other");
  const showInterventionOther = (values.interventions || []).includes("Other");

  return (
    <div className={PATIENT_PAGE_SHELL}>
      {!printMode && (
        <FormPageHeader
          patientName={patientName}
          mrn={patient?.mrn}
          status={form.status}
          onBack={onBack}
        />
      )}

      <div
        className={`${SHEET} bg-white rounded-xl border border-slate-200 shadow-sm px-5 sm:px-8 py-6 space-y-3`}
      >
        <header className="text-center pb-1">
          <h1 className="text-[13px] font-bold uppercase tracking-[0.16em] text-slate-800">
            Occupational Therapy Visit Note
          </h1>
          <p className="mt-1 text-[11px] text-slate-500">
            Home Health Clinical Documentation
          </p>
          <span className="mx-auto mt-2 block h-0.5 w-14 rounded-full bg-indigo-300" />
        </header>

        <FormIdentityStrip
          form={form}
          patientName={patientName || form.patientName}
          mrn={patient?.mrn}
          dateOfBirth={displayDateUS(
            patient?.dateOfBirth ||
              values.commonHeader?.dateOfBirth ||
              form.patientDateOfBirth,
          )}
          caregiverSlot={caregiverSlot}
          dateLabel="Visit Date"
          dateValue={displayDateUS(values.visitDate)}
          dateSlot={
            printMode ? null : (
              <div className="min-w-0">
                <div className="text-[7px] font-semibold uppercase tracking-wider text-slate-400">
                  Visit Date
                </div>
                <InputDatePicker
                  name="visitDate"
                  formatSeparator="/"
                  allowManualEntry
                  compact
                  disabled={!canEdit}
                  value={formatDateForInput(values.visitDate) || ""}
                  onChange={(event) => setValue("visitDate", event.target.value)}
                  max={VISIT_DATE_MAX}
                  className="mt-0.5 min-w-0"
                  inputClassName="w-full h-4 text-[10px] font-bold bg-transparent border-0 p-0 outline-none"
                />
              </div>
            )
          }
          visitMeta={buildOtVisitMetaProps({
            values,
            setValue,
            setValues,
            canEdit,
            printMode,
          })}
        />

        <FormMetaRow form={form} printMode={printMode} />
        {!printMode && <LockedReasonBanner reason={permissions.lockedReason} />}

        <OtNotesCommonHeader
          value={values.commonHeader}
          canEdit={canEdit}
          printMode={printMode}
          onChange={(next) => setValue("commonHeader", next)}
        />

        <Section icon={MedicalNotesIcon} title="7. Reason for Visit / Since Last Visit">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="min-w-0">
              <SubHeading>Visit Type</SubHeading>
              <CheckboxGrid
                options={VISIT_TYPE_OPTIONS}
                values={values.visitTypes}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("visitTypes", opt)}
                columns={2}
              />
            </div>
            <div className="min-w-0">
              <SubHeading>Reason for Visit</SubHeading>
              <CheckboxGrid
                options={REASON_FOR_VISIT_OPTIONS}
                values={values.reasonForVisit}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("reasonForVisit", opt)}
                columns={2}
              />
              {showReasonOther ? (
                <input
                  type="text"
                  className={`${fieldClass} mt-1.5`}
                  value={values.reasonForVisitOther || ""}
                  disabled={!canEdit}
                  placeholder="Specify other reason…"
                  onChange={(e) => setValue("reasonForVisitOther", e.target.value)}
                />
              ) : null}
            </div>
          </div>
          <div className="mt-3">
            <Field label="Since Last Visit (Patient/Caregiver Report)">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.sinceLastVisit || ""}
                disabled={!canEdit}
                placeholder="Describe status change, compliance, or concerns…"
                onChange={(e) => setValue("sinceLastVisit", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section icon={HelpingIcon} title="8. Subjective Report">
          <Field label="Subjective Statement">
            <textarea
              rows={3}
              className={textareaClass}
              value={values.subjectiveStatement || ""}
              disabled={!canEdit}
              placeholder="Patient report of symptoms / functional changes…"
              onChange={(e) => setValue("subjectiveStatement", e.target.value)}
            />
          </Field>
          <div className="mt-3 grid grid-cols-1 lg:grid-cols-2 gap-3">
            <PainScalePills
              label="Pre Pain (0–10)"
              name="painPreVisit"
              value={values.painPreVisit}
              canEdit={canEdit}
              onChange={(v) => setValue("painPreVisit", v)}
            />
            <PainScalePills
              label="Post Pain (0–10)"
              name="painPostVisit"
              value={values.painPostVisit}
              canEdit={canEdit}
              onChange={(v) => setValue("painPostVisit", v)}
            />
          </div>
          <div className="mt-3 max-w-xs">
            <Field label="Location">
              <input
                type="text"
                className={fieldClass}
                value={values.painLocation || ""}
                disabled={!canEdit}
                placeholder="e.g. R Shoulder"
                onChange={(e) => setValue("painLocation", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <Section
          icon={RunningIcon}
          title="9. Objective – Measurements & Functional Assessment"
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2.5 items-stretch">
            <Panel title="ADL/IADL Performance (Scoring)">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-[11px]">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="py-1 pr-2 font-semibold text-slate-500">Activity</th>
                      <th className="py-1 font-semibold text-slate-500 w-[7rem]">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {OT_VISIT_ADL_TASKS.map((task) => (
                      <tr key={task} className="border-b border-slate-50">
                        <td className="py-1 pr-2 text-slate-700 font-medium">{task}</td>
                        <td className="py-1">
                          <SelectField
                            value={(values.adlScores || {})[task] || ""}
                            options={OT_VISIT_ADL_SCORE_OPTIONS}
                            canEdit={canEdit}
                            onChange={(v) => setAdlScore(task, v)}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>

            <Panel title="Upper Extremity Function">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-[11px]">
                  <thead>
                    <tr className="border-b border-slate-100">
                      <th className="py-1 pr-2 font-semibold text-slate-500">Metric</th>
                      <th className="py-1 pr-2 font-semibold text-slate-500">Right</th>
                      <th className="py-1 font-semibold text-slate-500">Left</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(values.ueFunction || []).map((row) => (
                      <tr key={row.metric} className="border-b border-slate-50">
                        <td className="py-1 pr-2 text-slate-700 font-medium align-middle">
                          {row.metric}
                        </td>
                        <td className="py-1 pr-2">
                          <input
                            type="text"
                            className={fieldClass}
                            value={row.right || ""}
                            disabled={!canEdit}
                            onChange={(e) =>
                              updateUeRow(row.metric, { right: e.target.value })
                            }
                          />
                        </td>
                        <td className="py-1">
                          <input
                            type="text"
                            className={fieldClass}
                            value={row.left || ""}
                            disabled={!canEdit}
                            onChange={(e) =>
                              updateUeRow(row.metric, { left: e.target.value })
                            }
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>

            <Panel title="Cognition / Perception">
              <ExclusiveChoiceGrid
                name="cognitionStatus"
                options={COGNITION_STATUS_OPTIONS}
                value={values.cognitionStatus || ""}
                canEdit={canEdit}
                onChange={(opt) => setValue("cognitionStatus", opt)}
                columns={2}
              />
              <textarea
                rows={3}
                className={`${textareaClass} mt-2`}
                value={values.cognitionNotes || ""}
                disabled={!canEdit}
                placeholder="Notes…"
                onChange={(e) => setValue("cognitionNotes", e.target.value)}
              />
            </Panel>
          </div>
        </Section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
          <Section icon={HeartPulseIcon} title="10. Interventions Provided" className="h-full">
            <CheckboxGrid
              options={OT_VISIT_INTERVENTION_OPTIONS}
              values={values.interventions}
              canEdit={canEdit}
              onToggle={(opt) => toggleListValue("interventions", opt)}
              columns={2}
            />
            {showInterventionOther ? (
              <input
                type="text"
                className={`${fieldClass} mt-1.5`}
                value={values.interventionOther || ""}
                disabled={!canEdit}
                placeholder="Other intervention…"
                onChange={(e) => setValue("interventionOther", e.target.value)}
              />
            ) : null}
            <div className="mt-3">
              <Field label="Details">
                <textarea
                  rows={4}
                  className={textareaClass}
                  value={values.interventionDetails || ""}
                  disabled={!canEdit}
                  placeholder="Specific techniques, equipment used, or education provided…"
                  onChange={(e) => setValue("interventionDetails", e.target.value)}
                />
              </Field>
            </div>
          </Section>

          <Section icon={TargetIcon} title="11. Patient Response / Progress" className="h-full">
            <div className="space-y-3">
              <div>
                <SubHeading>Patient Response to Treatment</SubHeading>
                <ExclusiveChoiceGrid
                  name="patientResponse"
                  options={PATIENT_RESPONSE_OPTIONS}
                  value={values.patientResponse || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("patientResponse", opt)}
                  columns={3}
                />
              </div>
              <div>
                <SubHeading>Progress Toward Goals</SubHeading>
                <ExclusiveChoiceGrid
                  name="progressTowardGoals"
                  options={PROGRESS_TOWARD_GOALS_OPTIONS}
                  value={values.progressTowardGoals || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("progressTowardGoals", opt)}
                  columns={2}
                />
              </div>
              <div>
                <SubHeading>Functional Improvement</SubHeading>
                <ExclusiveChoiceGrid
                  name="functionalImprovement"
                  options={YES_NO_SOME_OPTIONS}
                  value={values.functionalImprovement || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("functionalImprovement", opt)}
                  columns={3}
                />
              </div>
              <Field label="Comments / Observations">
                <textarea
                  rows={3}
                  className={textareaClass}
                  value={values.responseComments || ""}
                  disabled={!canEdit}
                  onChange={(e) => setValue("responseComments", e.target.value)}
                />
              </Field>
            </div>
          </Section>
        </div>

        <Section icon={RunningIcon} title="12. Current Functional Status">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="min-w-0 space-y-3">
              <div>
                <SubHeading>Mobility</SubHeading>
                <ExclusiveChoiceGrid
                  name="mobilityTrend"
                  options={MOBILITY_TREND_OPTIONS}
                  value={values.mobilityTrend || ""}
                  canEdit={canEdit}
                  onChange={(opt) => setValue("mobilityTrend", opt)}
                  columns={3}
                />
              </div>
              <Field label="Transfers">
                <SelectField
                  value={values.transfersStatus}
                  options={TRANSFER_STATUS_OPTIONS}
                  canEdit={canEdit}
                  onChange={(v) => setValue("transfersStatus", v)}
                />
              </Field>
            </div>
            <div className="min-w-0 space-y-3">
              <Field label="Ambulation">
                <SelectField
                  value={values.ambulationStatus}
                  options={AMBULATION_STATUS_OPTIONS}
                  canEdit={canEdit}
                  onChange={(v) => setValue("ambulationStatus", v)}
                />
              </Field>
              <div>
                <SubHeading>ADLs Impacted</SubHeading>
                <CheckboxGrid
                  options={ADL_IMPACTED_OPTIONS}
                  values={values.adlsImpacted}
                  canEdit={canEdit}
                  onToggle={(opt) => toggleListValue("adlsImpacted", opt)}
                  columns={2}
                />
              </div>
            </div>
            <div className="min-w-0">
              <SubHeading>Home Safety</SubHeading>
              <CheckboxGrid
                options={HOME_SAFETY_OPTIONS}
                values={values.homeSafety}
                canEdit={canEdit}
                onToggle={(opt) => toggleListValue("homeSafety", opt)}
                columns={1}
              />
            </div>
          </div>
        </Section>

        <Section icon={TargetIcon} title="13. Goals">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
            <div className="rounded-md border border-slate-200 bg-slate-50/40 p-3 space-y-2.5 min-w-0 h-full">
              <div>
                <SubHeading>Short-Term Goals (1–4 weeks)</SubHeading>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  Measurable objectives expected within 1–4 weeks.
                </p>
              </div>
              {(values.shortTermGoals || []).map((row, idx) => (
                <div key={row.id} className="flex gap-2 min-w-0">
                  <span
                    className="mt-1.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-[10px] font-bold text-indigo-700"
                    aria-hidden
                  >
                    {idx + 1}
                  </span>
                  <textarea
                    rows={2}
                    className={`${textareaClass} min-w-0 flex-1`}
                    value={row.text || ""}
                    disabled={!canEdit}
                    placeholder="Enter measurable short-term objective…"
                    aria-label={`Short-term goal ${idx + 1}`}
                    onChange={(e) =>
                      updateTextGoal("shortTermGoals", row.id, e.target.value)
                    }
                  />
                  {canEdit ? (
                    <button
                      type="button"
                      disabled={(values.shortTermGoals || []).length <= 1}
                      onClick={() =>
                        removeTextGoal("shortTermGoals", "st", row.id)
                      }
                      className="mt-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30"
                      aria-label={`Remove short-term goal ${idx + 1}`}
                    >
                      <DeleteIcon size={14} />
                    </button>
                  ) : null}
                </div>
              ))}
              {canEdit ? (
                <button
                  type="button"
                  onClick={() => addTextGoal("shortTermGoals", "st")}
                  className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-md border border-slate-200 text-slate-700 hover:border-indigo-300 hover:bg-white"
                >
                  <PlusIcon size={13} />
                  Add Goal
                </button>
              ) : null}
            </div>

            <div className="rounded-md border border-slate-200 bg-slate-50/40 p-3 space-y-2.5 min-w-0 h-full">
              <div>
                <SubHeading>Long-Term Goals (4+ weeks)</SubHeading>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  Discharge-level outcomes expected at 4+ weeks.
                </p>
              </div>
              {(values.longTermGoals || []).map((row, idx) => (
                <div key={row.id} className="flex gap-2 min-w-0">
                  <span
                    className="mt-1.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-700"
                    aria-hidden
                  >
                    {idx + 1}
                  </span>
                  <textarea
                    rows={2}
                    className={`${textareaClass} min-w-0 flex-1`}
                    value={row.text || ""}
                    disabled={!canEdit}
                    placeholder="Enter discharge-level objective…"
                    aria-label={`Long-term goal ${idx + 1}`}
                    onChange={(e) =>
                      updateTextGoal("longTermGoals", row.id, e.target.value)
                    }
                  />
                  {canEdit ? (
                    <button
                      type="button"
                      disabled={(values.longTermGoals || []).length <= 1}
                      onClick={() =>
                        removeTextGoal("longTermGoals", "lt", row.id)
                      }
                      className="mt-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30"
                      aria-label={`Remove long-term goal ${idx + 1}`}
                    >
                      <DeleteIcon size={14} />
                    </button>
                  ) : null}
                </div>
              ))}
              {canEdit ? (
                <button
                  type="button"
                  onClick={() => addTextGoal("longTermGoals", "lt")}
                  className="inline-flex items-center gap-1.5 h-8 px-3 text-xs font-semibold rounded-md border border-slate-200 text-slate-700 hover:border-indigo-300 hover:bg-white"
                >
                  <PlusIcon size={13} />
                  Add Goal
                </button>
              ) : null}
            </div>
          </div>
        </Section>

        <Section icon={FileCheckIcon} title="14. Plan / Treatment Plan">
          <div>
            <SubHeading>Continue OT Services?</SubHeading>
            <ExclusiveChoiceGrid
              name="continueOt"
              options={YES_NO_OPTIONS}
              value={values.continueOt || ""}
              canEdit={canEdit}
              onChange={(opt) => setValue("continueOt", opt)}
              columns={2}
            />
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
            <Field label="Frequency">
              <input
                type="text"
                className={fieldClass}
                value={values.frequency || ""}
                disabled={!canEdit}
                placeholder="e.g., 3x/week"
                onChange={(e) => setValue("frequency", e.target.value)}
              />
            </Field>
            <Field label="Duration">
              <input
                type="text"
                className={fieldClass}
                value={values.duration || ""}
                disabled={!canEdit}
                placeholder="e.g., 4 weeks"
                onChange={(e) => setValue("duration", e.target.value)}
              />
            </Field>
            <Field label="Next Visit Date">
              <DateFieldInput
                name="nextVisitDate"
                value={values.nextVisitDate}
                canEdit={canEdit}
                printMode={printMode}
                onChange={(v) => setValue("nextVisitDate", v)}
              />
            </Field>
          </div>
          <div className="mt-3">
            <Field label="Specific Interventions Planned">
              <textarea
                rows={3}
                className={textareaClass}
                value={values.interventionsPlanned || ""}
                disabled={!canEdit}
                placeholder="Outline focus for next session…"
                onChange={(e) => setValue("interventionsPlanned", e.target.value)}
              />
            </Field>
          </div>
        </Section>

        <FormSignatureSections
          {...signatureProps}
          canSign={canSign}
          printMode={printMode}
          showPatientSection
          clinicianTitle="OT Authentication"
          clinicianName={values.clinicianSignatureName}
          onClinicianSign={handleClinicianSign}
          onPatientSign={signatures.handlePatientSign}
        />

        {isReviewer && !printMode ? (
          <OfficeReviewSection
            value={values.reviewComments}
            onChange={(v) => setValue("reviewComments", v)}
          />
        ) : null}

        {(!isReviewer || printMode) && (
          <OfficeCommentsNote comments={form.reviewComments} />
        )}
      </div>

      {(permissions.canSendToOffice ||
        permissions.canToBeSign ||
        permissions.canReview ||
        permissions.canEdit) && (
        <FormActionBar
          permissions={permissions}
          status={form.status}
          canEdit={canEdit}
          saving={saving}
          onSubmit={submit}
          printMode={printMode}
        />
      )}
    </div>
  );
};

OtVisitNoteForm.propTypes = {
  patient: PropTypes.object,
  clinicalForm: PropTypes.object.isRequired,
  draft: PropTypes.object,
  onBack: PropTypes.func.isRequired,
  onCreated: PropTypes.func,
  printMode: PropTypes.bool,
  onReady: PropTypes.func,
};

export default OtVisitNoteForm;
